import { phone } from 'phone';

export enum PhoneRegion {
  Japan = 'JP',
  UnitedKingdom = 'GB',
  Ireland = 'IE',
  Other = 'OTHER'
}

export interface PhoneRegionOption {
  id: PhoneRegion;
  name: string;
}

export const validatePhone = (phoneString: string, region: PhoneRegion = PhoneRegion.Other) => {
  if (!phoneString) return true;
  const normalized = normalizePhoneNumber(phoneString, region);

  if (!normalized) {
    return false;
  }

  if (region === PhoneRegion.Other && !normalized.startsWith('+')) {
    return false;
  }

  return phone(normalized).isValid;
};

export const formatPhoneNumber = (number?: string) => {
  if (!number) {
    return number;
  }

  const digits = number.replace(/\D/g, '');
  const hasLeadingPlus = number.trim().startsWith('+');
  const formatted = hasLeadingPlus ? `+${digits}` : digits;

  return formatted.substring(0, 16);
};

export const normalizePhoneNumber = (number: string | undefined, region: PhoneRegion = PhoneRegion.Other) => {
  const formatted = formatPhoneNumber(number);

  if (!formatted) {
    return formatted;
  }

  if (formatted.startsWith('+')) {
    return formatted;
  }

  if (region !== PhoneRegion.Other) {
    if (formatted.startsWith('81')) {
      return `+${formatted}`;
    }

    if (formatted.startsWith('44')) {
      return `+${formatted}`;
    }

    if (formatted.startsWith('353')) {
      return `+${formatted}`;
    }
  }

  switch (region) {
    case PhoneRegion.Japan:
      return formatted.startsWith('0') ? `+81${formatted.substring(1)}` : formatted;
    case PhoneRegion.UnitedKingdom:
      return formatted.startsWith('0') ? `+44${formatted.substring(1)}` : formatted;
    case PhoneRegion.Ireland:
      return formatted.startsWith('0') ? `+353${formatted.substring(1)}` : formatted;
    case PhoneRegion.Other:
    default:
      return formatted;
  }
};

export const denormalizePhoneNumber = (number: string | undefined, region: PhoneRegion = PhoneRegion.Other) => {
  const formatted = formatPhoneNumber(number);

  if (!formatted) {
    return formatted;
  }

  if (region === PhoneRegion.Japan && formatted.startsWith('+81')) {
    return `0${formatted.substring(3)}`;
  }

  if (region === PhoneRegion.UnitedKingdom && formatted.startsWith('+44')) {
    return `0${formatted.substring(3)}`;
  }

  if (region === PhoneRegion.Ireland && formatted.startsWith('+353')) {
    return `0${formatted.substring(4)}`;
  }

  return formatted;
};

export const inferPhoneRegion = (number?: string): PhoneRegion => {
  const formatted = formatPhoneNumber(number);

  if (!formatted) {
    return PhoneRegion.Other;
  }

  if (formatted.startsWith('+81') || formatted.startsWith('81')) {
    return PhoneRegion.Japan;
  }

  if (formatted.startsWith('+44') || formatted.startsWith('44')) {
    return PhoneRegion.UnitedKingdom;
  }

  if (formatted.startsWith('+353') || formatted.startsWith('353')) {
    return PhoneRegion.Ireland;
  }

  return PhoneRegion.Other;
};
